import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  TrendingUp, 
  Target, 
  CheckCircle2,
  Clock,
  GraduationCap,
  Award,
  RefreshCw,
  Download,
  Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DevelopmentPipelineTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [viewBy, setViewBy] = useState("all");

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("executiveToken") || localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/executive-enhanced/development-pipeline`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching pipeline data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { funnel, byRegion, totalEmployees, totalAssessed } = data;

  const funnelStages = [
    { key: "assessed", label: "Assessed", color: "#1a3a3a", icon: CheckCircle2 },
    { key: "devPlanAssigned", label: "Dev Plan Assigned", color: "#1565C0", icon: Target },
    { key: "trainingEnrolled", label: "Training Enrolled", color: "#7C3AED", icon: GraduationCap },
    { key: "activelyTraining", label: "Actively Training", color: "#E65100", icon: TrendingUp },
    { key: "milestoneAchieved", label: "Milestone Achieved", color: "#1B5E20", icon: Award }
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">Workforce Development Pipeline</h2>
          <p className="text-sm text-muted-foreground">Track employee progression through development stages</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={viewBy} onValueChange={setViewBy}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="View by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="segment">By Segment</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        {funnelStages.map((stage) => {
          const Icon = stage.icon;
          const stageData = funnel[stage.key];
          return (
            <Card key={stage.key} className="border-0 shadow-sm">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${stage.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: stage.color }} />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground uppercase">{stage.label}</span>
                </div>
                <p className="text-2xl font-bold" style={{ color: stage.color }}>{stageData.count}</p>
                <p className="text-xs text-muted-foreground">{stageData.percentage}% of assessed</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Visual Funnel */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#C4A265]" />
            Development Funnel Visualization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 py-4">
            {funnelStages.map((stage, index) => {
              const stageData = funnel[stage.key];
              const maxCount = funnel.assessed.count || 1;
              const width = (stageData.count / maxCount) * 100;
              
              return (
                <div key={stage.key} className="flex items-center gap-4">
                  <div className="w-40 text-right text-sm font-medium text-muted-foreground">
                    {stage.label}
                  </div>
                  <div className="flex-1 relative">
                    <div 
                      className="h-10 rounded-lg flex items-center justify-center text-white font-bold transition-all duration-500"
                      style={{ 
                        width: `${Math.max(width, 15)}%`, 
                        backgroundColor: stage.color,
                        marginLeft: `${(100 - Math.max(width, 15)) / 2}%`
                      }}
                    >
                      {stageData.count} ({stageData.percentage}%)
                    </div>
                  </div>
                  <div className="w-20 text-left">
                    {index > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ↓ {funnel[funnelStages[index - 1].key].count - stageData.count}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Regional Breakdown */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-[#C4A265]" />
            Pipeline by Region
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-sm font-semibold">Region</th>
                  <th className="text-center p-3 text-sm font-semibold">Total</th>
                  <th className="text-center p-3 text-sm font-semibold">Assessed</th>
                  <th className="text-center p-3 text-sm font-semibold">Dev Plan</th>
                  <th className="text-center p-3 text-sm font-semibold">Enrolled</th>
                  <th className="text-center p-3 text-sm font-semibold">Training</th>
                  <th className="text-center p-3 text-sm font-semibold">Milestone</th>
                  <th className="text-center p-3 text-sm font-semibold">Completion %</th>
                </tr>
              </thead>
              <tbody>
                {byRegion.filter(r => r.region !== 'unknown').map((region, i) => {
                  const completion = region.total > 0 ? Math.round(region.assessed / region.total * 100) : 0;
                  const regionNames = {
                    usa: { name: 'USA (SLIC)', flag: '🇺🇸' },
                    canada: { name: 'Canada (ivari)', flag: '🇨🇦' },
                    jamaica: { name: 'Jamaica', flag: '🇯🇲' },
                    barbados: { name: 'Barbados', flag: '🇧🇧' },
                    trinidad_tobago: { name: 'Trinidad & Tobago', flag: '🇹🇹' },
                    panama: { name: 'Panama', flag: '🇵🇦' }
                  };
                  const display = regionNames[region.region] || { name: region.region, flag: '🌍' };
                  
                  return (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-medium">
                        <span className="mr-2">{display.flag}</span>
                        {display.name}
                      </td>
                      <td className="p-3 text-center">{region.total}</td>
                      <td className="p-3 text-center font-semibold text-[#1a3a3a]">{region.assessed}</td>
                      <td className="p-3 text-center text-[#1565C0]">{region.devPlanAssigned}</td>
                      <td className="p-3 text-center text-[#7C3AED]">{region.trainingEnrolled}</td>
                      <td className="p-3 text-center text-[#E65100]">{region.activelyTraining}</td>
                      <td className="p-3 text-center text-[#1B5E20]">{region.milestoneAchieved}</td>
                      <td className="p-3 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <Progress value={completion} className="w-16 h-2" />
                          <span className="text-sm font-medium">{completion}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td className="p-3">Total Organization</td>
                  <td className="p-3 text-center">{totalEmployees}</td>
                  <td className="p-3 text-center text-[#1a3a3a]">{totalAssessed}</td>
                  <td className="p-3 text-center text-[#1565C0]">{funnel.devPlanAssigned.count}</td>
                  <td className="p-3 text-center text-[#7C3AED]">{funnel.trainingEnrolled.count}</td>
                  <td className="p-3 text-center text-[#E65100]">{funnel.activelyTraining.count}</td>
                  <td className="p-3 text-center text-[#1B5E20]">{funnel.milestoneAchieved.count}</td>
                  <td className="p-3 text-center">
                    <Badge className="bg-[#C4A265]">{Math.round(totalAssessed / totalEmployees * 100)}%</Badge>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
